import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';

import EntityFormModal from '@sharedCR/common/EntityFormModal';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import { CUSTOM_FIELDS_TARGET } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { PendingChangesActionType } from 'constants/pendingChanges';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useRevertFieldValues from 'hooks/useRevertFieldValues';
import { calculatePendingChanges } from 'utils/pendingChanges';

import { selectMasterCompanyId } from 'store/auth/selectors';
import { loadCustomFieldsAction } from 'store/customFieldDefinitions/actions';
import {
  selectFinancialInfoAllTablesData,
  selectFinancialInfoBorrowers,
  selectHiddenFieldIds,
  selectHiddenPageIds,
  selectIsClosedHidden,
  selectSortedBorrowerAssets,
  selectSortedBorrowerIncomes,
  selectSortedBorrowerLiabilities,
  selectSortedBorrowerProperties,
} from 'store/loanApplications/selectors';
import { clearLocalEditedFieldsAction } from 'store/presence/actions';
import { selectLocalEditedFieldPaths } from 'store/presence/selectors';

import {
  ALL_BORROWERS_PATH,
  FINANCIAL_INFO_ACTION_TYPE,
  FINANCIAL_INFO_TABLE_KEY,
  FINANCIAL_INFO_VIEWS_PATHS,
  NEW_RECORD_DATA,
  NEW_RECORD_DEFAULT_APPLICATION_DATA,
} from '../FinancialInfo.constants';
import { generateFinancialInfoPath } from '../FinancialInfo.utils';

import Content from './Content';
import { Title } from './Content/AdditionalComponents';
import AdditionalNavigationContent from './Content/AdditionalComponents/AdditionalNavigationContent';
import useSaveBlockedHandler from './hooks/useSaveBlockedHandler';
import { getHeadTabs, getNavigationTabs } from './UpdateTableModal.config';
import { modalStyles } from './UpdateTableModal.styles';
import {
  getKeptClosedRecordIds,
  getVisibleTableData,
  shouldRedirectToClosest,
} from './UpdateTableModal.utils';

const UpdateTableModal = ({ isDataLoading, setIsLoading }) => {
  const history = useHistory();
  const {
    applicationId,
    tab,
    subtab: tablePath,
    entityId: borrowerId,
    section: actionType,
    subsection: recordId,
  } = useParams();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const revertFieldValues = useRevertFieldValues();

  const localEditedFieldPaths = useSelector(selectLocalEditedFieldPaths);
  const hiddenPageIds = useSelector(selectHiddenPageIds);
  const hiddenFieldIds = useSelector(selectHiddenFieldIds);
  const masterCompanyId = useSelector(selectMasterCompanyId);

  useEffectSafeStrictMode(() => {
    if (!masterCompanyId) {
      return;
    }

    dispatch(
      loadCustomFieldsAction({
        companyId: masterCompanyId,
        targets: [
          CUSTOM_FIELDS_TARGET.INCOME,
          CUSTOM_FIELDS_TARGET.ASSET,
          CUSTOM_FIELDS_TARGET.LIABILITY,
          CUSTOM_FIELDS_TARGET.PROPERTY,
        ],
        enabledOnly: true,
        merge: true,
      }),
    );
  }, [dispatch, masterCompanyId]);

  const [deletedRecords, setDeletedRecords] = useState(new Set());
  const [deletedInitialRecords, setDeletedInitialRecords] = useState(new Set());
  const [newAddedInitialRecords, setNewAddedInitialRecords] = useState(new Set());
  const [newRecords, setNewRecords] = useState([]);
  const [newRecordsOwners, setNewRecordsOwners] = useState({});
  const [isNestedEntityEditActive, setIsNestedEntityEditActive] = useState(false);

  const [pendingChanges, setPendingChanges] = useState({});
  const [pendingChangesCounts, setPendingChangesCounts] = useState({});
  const [validationErrorSummary, setValidationErrorSummary] = useState({});
  const [previouslyInvalidRecordIds, setPreviouslyInvalidRecordIds] = useState(new Set());

  const isClosedHidden = useSelector(selectIsClosedHidden);
  const borrowers = useSelector(selectFinancialInfoBorrowers);
  const incomes = useSelector(selectSortedBorrowerIncomes(borrowerId, true));
  const liabilities = useSelector(selectSortedBorrowerLiabilities(borrowerId, true));
  const assets = useSelector(selectSortedBorrowerAssets(borrowerId, true));
  const reo = useSelector(selectSortedBorrowerProperties(borrowerId, true));
  const allBorrowersIncomes = useSelector(selectSortedBorrowerIncomes(ALL_BORROWERS_PATH, true));
  const allBorrowersLiabilities = useSelector(
    selectSortedBorrowerLiabilities(ALL_BORROWERS_PATH, true),
  );
  const allBorrowersAssets = useSelector(selectSortedBorrowerAssets(ALL_BORROWERS_PATH, true));
  const allBorrowersReo = useSelector(selectSortedBorrowerProperties(ALL_BORROWERS_PATH, true));
  const rawTableData = useSelector(selectFinancialInfoAllTablesData)[
    FINANCIAL_INFO_TABLE_KEY[tablePath]
  ];

  const pathFromRef = useRef(state?.pathFrom);
  const allDeletedRecordsRef = useRef(new Set());

  const isCorrectPath =
    Object.values(FINANCIAL_INFO_ACTION_TYPE).includes(actionType) && !!recordId;
  const isAddNewRecordInitiated = recordId === NEW_RECORD_DATA.id;

  const isAllBorrowersTabVisible = borrowers.length > 1;

  useEffect(() => {
    if (!FINANCIAL_INFO_TABLE_KEY[tablePath]) {
      return;
    }

    const { deletedRecordsByInit, newAddedRecordsByInit } = rawTableData.reduce(
      (acc, { id, pendingChangesActionType }) => {
        if (pendingChangesActionType === PendingChangesActionType.DELETED) {
          acc.deletedRecordsByInit.add(id);
          allDeletedRecordsRef.current.add(id);
        }

        if (pendingChangesActionType === PendingChangesActionType.ADDED) {
          acc.newAddedRecordsByInit.add(id);
        }

        return acc;
      },
      { deletedRecordsByInit: new Set(), newAddedRecordsByInit: new Set() },
    );

    setDeletedInitialRecords(deletedRecordsByInit);
    setNewAddedInitialRecords(newAddedRecordsByInit);
  }, [rawTableData, tablePath]);

  const keptClosedRecordIds = useMemo(
    () =>
      getKeptClosedRecordIds({
        validationErrorSummary,
        recordId,
        isRevealed: state?.revealedRecordId === recordId,
        previouslyInvalidRecordIds,
      }),
    [validationErrorSummary, recordId, state, previouslyInvalidRecordIds],
  );

  const data = useMemo(() => {
    const recordsByTablePath = {
      [FINANCIAL_INFO_VIEWS_PATHS.INCOME_AND_EMPLOYMENT]: incomes,
      [FINANCIAL_INFO_VIEWS_PATHS.LIABILITIES]: liabilities,
      [FINANCIAL_INFO_VIEWS_PATHS.ASSETS]: assets,
      [FINANCIAL_INFO_VIEWS_PATHS.REO]: reo,
    };

    return getVisibleTableData({
      tablePath,
      records: recordsByTablePath[tablePath] ?? [],
      newRecords,
      isClosedHidden,
      keptRecordIds: keptClosedRecordIds,
    });
  }, [
    assets,
    incomes,
    liabilities,
    reo,
    tablePath,
    isClosedHidden,
    newRecords,
    keptClosedRecordIds,
  ]);

  const allBorrowersRecords = useMemo(() => {
    const recordsByTablePath = {
      [FINANCIAL_INFO_VIEWS_PATHS.INCOME_AND_EMPLOYMENT]: allBorrowersIncomes,
      [FINANCIAL_INFO_VIEWS_PATHS.LIABILITIES]: allBorrowersLiabilities,
      [FINANCIAL_INFO_VIEWS_PATHS.ASSETS]: allBorrowersAssets,
      [FINANCIAL_INFO_VIEWS_PATHS.REO]: allBorrowersReo,
    };

    return recordsByTablePath[tablePath] ?? [];
  }, [
    allBorrowersIncomes,
    allBorrowersLiabilities,
    allBorrowersAssets,
    allBorrowersReo,
    tablePath,
  ]);

  const allRecords = useMemo(
    () => [...allBorrowersRecords, ...newRecords],
    [allBorrowersRecords, newRecords],
  );

  const allVisibleRecords = useMemo(
    () =>
      getVisibleTableData({
        tablePath,
        records: allBorrowersRecords,
        newRecords,
        isClosedHidden,
        keptRecordIds: new Set(Object.keys(validationErrorSummary)),
      }),
    [allBorrowersRecords, newRecords, isClosedHidden, tablePath, validationErrorSummary],
  );

  const onCountPendingChanges = useCallback(pendingChanges => {
    setPendingChangesCounts(calculatePendingChanges(pendingChanges, true));
  }, []);

  const onValidationErrorSummaryChange = useCallback(nextSummary => {
    setValidationErrorSummary(prevSummary =>
      _.isEqual(prevSummary, nextSummary) ? prevSummary : nextSummary,
    );
    setPreviouslyInvalidRecordIds(prevIds => {
      const newIds = Object.keys(nextSummary).filter(id => !prevIds.has(id));

      return newIds.length ? new Set([...prevIds, ...newIds]) : prevIds;
    });
  }, []);

  const onSaveBlocked = useSaveBlockedHandler({
    applicationId,
    tab,
    tablePath,
    actionType,
    recordId,
    borrowerId,
    data,
    allVisibleRecords,
    allRecords,
    isClosedHidden,
    isAllBorrowersTabVisible,
    hiddenPageIds,
    hiddenFieldIds,
    isNestedEntityEditActive,
    pathFromRef,
  });

  const getHrefToRedirect = useCallback(
    recordId =>
      generateFinancialInfoPath({
        applicationId,
        tab,
        tablePath,
        borrowerId,
        actionType,
        recordId,
        pathFrom: pathFromRef.current,
      }),
    [applicationId, borrowerId, tab, tablePath, actionType],
  );

  const onResetBeforeClosing = useCallback(() => {
    setDeletedRecords(new Set());
    setDeletedInitialRecords(new Set());
    setNewAddedInitialRecords(new Set());
    setNewRecords([]);
    setNewRecordsOwners({});
    setValidationErrorSummary({});
    setPreviouslyInvalidRecordIds(new Set());
    allDeletedRecordsRef.current = new Set();

    pathFromRef.current = null;
  }, []);

  const redirectToClosestNotDeletedRecord = useCallback(
    (isExecutedAfterDeletion = false) => {
      const closestNotDeletedRecord =
        data.find(({ id }) => !allDeletedRecordsRef.current.has(id)) ??
        (isExecutedAfterDeletion ? null : data[0]);

      if (!closestNotDeletedRecord) {
        return;
      }

      history.push(getHrefToRedirect(closestNotDeletedRecord.id));
    },
    [history, getHrefToRedirect, data],
  );

  const updateNewRecordOwners = useCallback((id, owners) => {
    setNewRecordsOwners(prev => ({ ...prev, [id]: owners }));
  }, []);

  const onDeleteRecord = useCallback(
    id => {
      if (id.startsWith(NEW_RECORD_DATA.id)) {
        updateNewRecordOwners(recordId, []);
        setNewRecords(prev => prev.filter(newRecord => newRecord.id !== id));

        return;
      }

      allDeletedRecordsRef.current.add(id);
      setDeletedRecords(prev => new Set(Array.from(prev)).add(id));

      if (recordId === id) {
        redirectToClosestNotDeletedRecord(true);
      }
    },
    [recordId, redirectToClosestNotDeletedRecord, updateNewRecordOwners],
  );

  const onRestoreRecord = useCallback(
    id => {
      setDeletedRecords(prev => {
        if (!prev.has(id)) {
          return prev;
        }

        const updatedDeletedRecords = new Set(Array.from(prev));

        updatedDeletedRecords.delete(id);
        allDeletedRecordsRef.current.delete(id);

        return updatedDeletedRecords;
      });

      if (recordId !== id) {
        history.push(getHrefToRedirect(id));
      }
    },
    [history, getHrefToRedirect, recordId],
  );

  const headTabs = useMemo(
    () =>
      getHeadTabs({
        applicationId,
        tab,
        tablePath,
        actionType,
        recordId,
        borrowers,
        isAllBorrowersTabVisible,
        pathFrom: pathFromRef.current,
        history,
        pendingChangesCounts,
        newRecordsOwners,
        validationErrorSummary,
        isNestedEntityEditActive,
        setIsNestedEntityEditActive,
        dispatch,
      }),
    [
      actionType,
      applicationId,
      borrowers,
      tab,
      tablePath,
      recordId,
      isAllBorrowersTabVisible,
      history,
      pendingChangesCounts,
      newRecordsOwners,
      validationErrorSummary,
      isNestedEntityEditActive,
      dispatch,
    ],
  );

  const navigationTabs = useMemo(
    () =>
      getNavigationTabs({
        applicationId,
        tab,
        tablePath,
        borrowerId,
        actionType,
        data,
        pathFrom: pathFromRef.current,
        history,
        onDeleteRecord,
        onRestoreRecord,
        deletedRecords,
        deletedInitialRecords,
        newAddedInitialRecords,
        pendingChangesCounts,
        validationErrorSummary,
        setIsNestedEntityEditActive,
        isNestedEntityEditActive,
        dispatch,
        hiddenPageIds,
        hiddenFieldIds,
      }),
    [
      applicationId,
      tab,
      tablePath,
      borrowerId,
      actionType,
      data,
      history,
      onDeleteRecord,
      onRestoreRecord,
      deletedRecords,
      deletedInitialRecords,
      newAddedInitialRecords,
      pendingChangesCounts,
      validationErrorSummary,
      isNestedEntityEditActive,
      dispatch,
      hiddenPageIds,
      hiddenFieldIds,
    ],
  );

  const additionalProps = useMemo(
    () => ({
      deletedRecords,
      deletedInitialRecords,
      newRecords,
      updateNewRecordOwners,
      setNewRecords,
      newAddedInitialRecords,
      onCountPendingChanges,
      setPendingChanges,
      onSaveBlocked,
      onValidationErrorSummaryChange,
    }),
    [
      deletedRecords,
      deletedInitialRecords,
      newRecords,
      updateNewRecordOwners,
      newAddedInitialRecords,
      onCountPendingChanges,
      setPendingChanges,
      onSaveBlocked,
      onValidationErrorSummaryChange,
    ],
  );

  const onClose = useCallback(() => {
    if (localEditedFieldPaths.length) {
      revertFieldValues(localEditedFieldPaths);
      dispatch(clearLocalEditedFieldsAction());
    }

    const to =
      pathFromRef.current ??
      LoansPaths.applicationManagement.generatePath({
        applicationId,
        tab,
        subtab: FINANCIAL_INFO_VIEWS_PATHS.ALL_VIEWS,
        entityId: ALL_BORROWERS_PATH,
      });

    onResetBeforeClosing();
    setTimeout(() => history.push(to), 0);
  }, [
    localEditedFieldPaths,
    applicationId,
    tab,
    onResetBeforeClosing,
    revertFieldValues,
    dispatch,
    history,
  ]);

  const onAddNewRecord = useCallback(() => {
    setNewRecords(prev => {
      const lastNewRecordNumber = Number(prev.slice(-1)[0]?.id?.slice(-1) ?? 0);

      const newRecordId = `${NEW_RECORD_DATA.id}-${lastNewRecordNumber + 1}`;

      setTimeout(() => history.push(getHrefToRedirect(newRecordId)));

      return [
        ...prev,
        { ...NEW_RECORD_DATA, id: newRecordId, ...NEW_RECORD_DEFAULT_APPLICATION_DATA[tablePath] },
      ];
    });
  }, [history, getHrefToRedirect, tablePath]);

  useLayoutEffect(() => {
    if (!isAddNewRecordInitiated) {
      return;
    }

    onAddNewRecord();
  }, [isAddNewRecordInitiated, onAddNewRecord]);

  useEffect(() => {
    if (state?.pathFrom) {
      pathFromRef.current = state.pathFrom;
    }

    if (!actionType || isCorrectPath) {
      return;
    }

    onClose();
    setIsNestedEntityEditActive(false);
  }, [isCorrectPath, actionType, onClose, state]);

  useEffect(() => {
    if (!shouldRedirectToClosest({ data, recordId, isCorrectPath, isAddNewRecordInitiated })) {
      return;
    }

    redirectToClosestNotDeletedRecord();
  }, [
    recordId,
    getHrefToRedirect,
    data,
    isCorrectPath,
    isAddNewRecordInitiated,
    redirectToClosestNotDeletedRecord,
  ]);

  return (
    <EntityFormModal
      withOuterClickClose
      Content={Content}
      additionalNavigationContent={
        tablePath === FINANCIAL_INFO_VIEWS_PATHS.LIABILITIES ? (
          <AdditionalNavigationContent />
        ) : null
      }
      additionalProps={additionalProps}
      customStyles={modalStyles}
      headTabs={headTabs}
      headValue={borrowerId}
      isData={!!data.length}
      isLoading={isDataLoading}
      isOpen={!!isCorrectPath}
      navigationTabs={navigationTabs}
      navigationValue={isAddNewRecordInitiated ? null : recordId}
      setIsLoading={setIsLoading}
      setIsNestedEntityEditActive={setIsNestedEntityEditActive}
      title={
        <Title
          pendingChanges={pendingChanges}
          tablePath={tablePath ?? ''}
          onAddNewRecord={onAddNewRecord}
        />
      }
      onClose={onClose}
    />
  );
};

UpdateTableModal.propTypes = {
  isDataLoading: PropTypes.bool.isRequired,
  ...WithLoadingPropTypes,
};

export default withLoading(memo(UpdateTableModal));
